import React, { useState } from "react";
import ReactSelect, { createFilter } from "react-select";
import AsyncSelect from "react-select/async";
import AsyncCreatableSelect from "react-select/async-creatable";
import CreatableSelect from "react-select/creatable";
import { useTheme } from "styled-components";
import { Text } from "../Text";
import { LoadingSpinner, SpinnerSize } from "../LoadingSpinner";
import { customStyles } from "./reactSelect";
// Re-export here so that we don't need to add `react-select` to `web-client`.
export { components } from "react-select";
/**
 * A custom filter so that the searching/filtering behaviour works similarly
 * to how native select inputs work. This ensures that matches only match the
 * start of the option labels, which is what users expect.
 */
const filter = createFilter({
    ignoreAccents: true,
    ignoreCase: true,
    matchFrom: "start",
    trim: true,
});
const NoOptionsMessageContainer = (noOptionsMessage) => () => React.createElement(Text, null, noOptionsMessage);
/**
 * A TS Type Guard to determine if the `options` are grouped since TS can't infer this on its own.
 */
export const isGrouped = (option) => Object.prototype.hasOwnProperty.call(option, "options");
export const getSelectOptionByValue = (options, value) => {
    if (value === null) {
        return null;
    }
    if (!(options === null || options === void 0 ? void 0 : options.length) || value === undefined || (Array.isArray(value) && !(value === null || value === void 0 ? void 0 : value.length))) {
        return undefined;
    }
    // Create a map of options by value, so we can quickly look up the option.
    const optionsKeyedByValue = options.reduce((acc, option) => {
        if (isGrouped(option)) {
            option.options.forEach((groupedOption) => {
                acc.set(groupedOption.value, groupedOption);
            });
        }
        else {
            acc.set(option.value, option);
        }
        return acc;
    }, new Map());
    if (Array.isArray(value)) {
        const selectedOptions = value.map((val) => optionsKeyedByValue.get(val)).filter(Boolean);
        return selectedOptions.length ? selectedOptions : undefined;
    }
    return optionsKeyedByValue.get(value);
};
const LoadingIndicator = () => React.createElement(LoadingSpinner, { size: SpinnerSize.BUTTON, marginRight: "10px" });
/**
 * A customizable select component that supports async loading, creatable options, and various styling options
 * Built on top of react-select with additional functionality. We are using the [react-select](https://react-select.com/) library for
 * the base select component.
 *
 * @param props - The select component props
 * @returns A select component with the specified configuration
 */
export const Select = ({ "aria-label": ariaLabel, autoFocus = false, cacheOptions, components, descriptorId, defaultOptions, disabled = false, filterOptions = filter, inputId, inputValue, isClearable = false, isError = false, isMulti = false, isSearchable = true, loadOptions, creatable, name, noOptionsMessage, onBlur, onChange, onInputChange, onKeyDown, openMenuOnFocus, options, placeholder, readOnly = false, required = false, showLoadingState = false, value, }) => {
    // Determine which Select component variant to use based on the combination of props
    // AsyncCreatableSelect - Supports both async loading and option creation
    // AsyncSelect - Supports async loading only
    // CreatableSelect - Supports option creation only
    // ReactSelect - Basic select functionality
    let SelectComponent;
    if (loadOptions && creatable) {
        SelectComponent = AsyncCreatableSelect;
    }
    else if (loadOptions) {
        SelectComponent = AsyncSelect;
    }
    else if (creatable) {
        SelectComponent = CreatableSelect;
    }
    else {
        SelectComponent = ReactSelect;
    }
    // Track menu open state for keyboard event handling
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    /**
     * Handle keyboard events on the select component
     * Prevents escape key from bubbling up when menu is open
     * Allows custom keyboard event handling via onKeyDown prop
     */
    const handleKeyDown = (event) => {
        if (onKeyDown) {
            onKeyDown(event, isMenuOpen);
        }
        if ((event.key === "Escape" || event.key === "Esc") && isMenuOpen) {
            event.stopPropagation();
        }
    };
    // Get theme for styling
    const theme = useTheme();
    // Define error styles to be applied when isError is true
    const errorStyle = {
        control: {
            borderColor: theme.colors.inputColors.inputErrorBorder,
        },
    };
    // Use options prop if available, otherwise fall back to defaultOptions if it's an array
    const valueOptions = options !== null && options !== void 0 ? options : (typeof defaultOptions !== "boolean" ? defaultOptions : undefined);
    const mergedComponents = Object.assign({ LoadingIndicator }, components);
    return (React.createElement(SelectComponent, { "aria-label": ariaLabel, "aria-describedby": descriptorId, autoFocus: autoFocus, cacheOptions: cacheOptions, closeMenuOnSelect: !isMulti, defaultOptions: defaultOptions, 
        // Only apply custom filter if not using async loading
        filterOption: loadOptions ? undefined : filterOptions, inputId: inputId, inputValue: inputValue, loadOptions: loadOptions, isClearable: isClearable, 
        // Since `react-select` doesn't have a `readOnly` prop we treat `disabled` and `readOnly` the same way.
        isDisabled: disabled || readOnly, isLoading: showLoadingState, isMulti: isMulti, 
        // Disable individual options based on their disabled property
        isOptionDisabled: (option) => isGrouped(option) ? false : Boolean(option === null || option === void 0 ? void 0 : option.disabled), isSearchable: isSearchable, name: name, 
        // Render custom no options message if provided
        noOptionsMessage: noOptionsMessage ? NoOptionsMessageContainer(noOptionsMessage) : undefined, onBlur: onBlur, onChange: onChange, onInputChange: onInputChange, onKeyDown: handleKeyDown, 
        // Track menu open state
        onMenuClose: () => setIsMenuOpen(false), onMenuOpen: () => setIsMenuOpen(true), openMenuOnFocus: openMenuOnFocus, options: options, placeholder: placeholder, required: required, styles: customStyles(theme, isError ? errorStyle : undefined), value: getSelectOptionByValue(valueOptions, value), components: mergedComponents }));
};
